import type { Weekday } from "../../utils/scheduleTypes";
import type { StoryScheduleWeekdayApi } from "./storySchedule.types";

/**
 * Weekday vocabulary bridge for the Story refresh-schedule API.
 *
 * The app uses 3-letter identifiers (`"mon"`) shared across DeliverStory,
 * SchedulePicker and the story refresh modal; the backend uses full lowercase
 * names (`"monday"`). Neither side can move: the 3-letter form is baked into
 * several schedule editors, and the full form is the published contract.
 *
 * Kept in its own module so the translation is a single, testable choke point —
 * a stray `"mon"` reaching the wire is a 422 with no useful message.
 */

const TO_API: Record<Weekday, StoryScheduleWeekdayApi> = {
  sun: "sunday",
  mon: "monday",
  tue: "tuesday",
  wed: "wednesday",
  thu: "thursday",
  fri: "friday",
  sat: "saturday",
};

const FROM_API: Record<StoryScheduleWeekdayApi, Weekday> = {
  sunday: "sun",
  monday: "mon",
  tuesday: "tue",
  wednesday: "wed",
  thursday: "thu",
  friday: "fri",
  saturday: "sat",
};

/** App weekday → API weekday. */
export const toApiWeekday = (weekday: Weekday): StoryScheduleWeekdayApi =>
  TO_API[weekday];

/** API weekday → app weekday. */
export const fromApiWeekday = (weekday: StoryScheduleWeekdayApi): Weekday =>
  FROM_API[weekday];

/**
 * Maps a `repeat_on` list to the app vocabulary, dropping anything the backend
 * sends that isn't a weekday we recognise. Dropping beats throwing here: an
 * unknown day should not make an otherwise-editable schedule unopenable.
 */
export const fromApiWeekdays = (
  weekdays: readonly StoryScheduleWeekdayApi[],
): Weekday[] => weekdays.map((day) => FROM_API[day]).filter(Boolean);
